import type { Column, Table as ReactTable, RowData } from "@tanstack/react-table";
import type { CSSProperties } from "react";

const DEFAULT_PINNED_DIVIDER_COLOR = "var(--color-neutral-300, #c5d0e1)";

function sumColumnSizes<TData extends RowData>(columns: Array<Column<TData, unknown>>): number {
  return columns.reduce((total, column) => total + column.getSize(), 0);
}

/**
 * Compensa, via scroll-margin, a largura ocupada pelas colunas fixas para que o foco nativo
 * não considere visível uma célula coberta por uma coluna sticky.
 */
export function getStickyScrollMarginStyle<TData extends RowData>(
  table: ReactTable<TData>
): CSSProperties | undefined {
  const scrollMarginInlineStart = sumColumnSizes(table.getLeftVisibleLeafColumns());
  const scrollMarginInlineEnd = sumColumnSizes(table.getRightVisibleLeafColumns());

  if (!scrollMarginInlineStart && !scrollMarginInlineEnd) return undefined;

  return { scrollMarginInlineStart, scrollMarginInlineEnd };
}

/** Retorna uma largura fixa, necessária para manter os offsets das colunas sticky alinhados. */
export function getColumnSizeStyle<TData extends RowData>(
  column: Column<TData, unknown>
): CSSProperties {
  const size = column.getSize();
  return { width: size, minWidth: size, maxWidth: size };
}

/** Estilo sticky para colunas fixadas via `column.pin()` ou `initialColumnPinning`. */
export function getPinnedColumnStyle<TData extends RowData>(
  column: Column<TData, unknown>,
  table: ReactTable<TData>
): CSSProperties | undefined {
  const pinned = column.getIsPinned();
  if (!pinned) return undefined;

  const leftColumns = table.getLeftVisibleLeafColumns();
  const isGroupBoundary =
    pinned === "left"
      ? leftColumns[leftColumns.length - 1]?.id === column.id
      : table.getRightVisibleLeafColumns()[0]?.id === column.id;
  let boxShadow: CSSProperties["boxShadow"];

  if (isGroupBoundary) {
    if (pinned === "left") {
      boxShadow = `2px 0 4px -2px ${DEFAULT_PINNED_DIVIDER_COLOR}`;
    } else if (pinned === "right") {
      boxShadow = `-2px 0 4px -2px ${DEFAULT_PINNED_DIVIDER_COLOR}`;
    }
  }

  return {
    position: "sticky",
    left: pinned === "left" ? column.getStart("left") : undefined,
    right: pinned === "right" ? column.getAfter("right") : undefined,
    width: column.getSize(),
    minWidth: column.getSize(),
    zIndex: 1,
    boxShadow,
  };
}
